import React from 'react';
import { useTranslation } from 'react-i18next';
import FadeInSection from './FadeInSection';

const BusinessSection: React.FC = () => {
  const { t } = useTranslation('website');

  const businesses = [
    {
      img: '/images/biz-digital-human.png',
      icon: 'scan-face',
      iconColor: 'text-accent-400',
      titleKey: 'business.digitalHuman.title',
      descKey: 'business.digitalHuman.desc',
      tagsKey: 'business.digitalHuman.tags',
      fallbackIcon: 'user-circle',
      fallbackGrad: 'from-primary-900/40',
      alt: 'Digital Human',
    },
    {
      img: '/images/biz-aigc.png',
      icon: 'wand-sparkles',
      iconColor: 'text-accent-400',
      titleKey: 'business.aigc.title',
      descKey: 'business.aigc.desc',
      tagsKey: 'business.aigc.tags',
      fallbackIcon: 'wand-sparkles',
      fallbackGrad: 'from-purple-900/40',
      alt: 'AIGC',
    },
    {
      img: '/images/biz-ai-agent.png',
      icon: 'brain-circuit',
      iconColor: 'text-accent-400',
      titleKey: 'business.aiAgent.title',
      descKey: 'business.aiAgent.desc',
      tagsKey: 'business.aiAgent.tags',
      fallbackIcon: 'bot',
      fallbackGrad: 'from-indigo-900/40',
      alt: 'AI Agent',
    },
  ];

  return (
    <section id="business" className="py-24 lg:py-32 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <FadeInSection className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-8 h-[2px] bg-accent-500" />
            <span className="text-accent-400 text-xs font-semibold tracking-wider uppercase">{t('business.label')}</span>
            <div className="w-8 h-[2px] bg-accent-500" />
          </div>
          <h2 className="text-3xl lg:text-4xl font-extrabold text-white mb-4">{t('business.title')}</h2>
          <p className="text-dark-400 max-w-2xl mx-auto" style={{ lineHeight: '1.9' }}>{t('business.subtitle')}</p>
        </FadeInSection>

        {/* Cards */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {businesses.map((biz, i) => (
            <FadeInSection key={i} delay={`fade-in-delay-${i}` as any} className="card-hover group rounded-3xl bg-dark-900/60 border border-dark-800/50 overflow-hidden">
              <div className="aspect-[16/10] relative overflow-hidden img-overlay">
                <img
                  src={biz.img}
                  alt={biz.alt}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                    const fallback = (e.target as HTMLImageElement).nextElementSibling as HTMLElement;
                    if (fallback) fallback.style.display = 'flex';
                  }}
                />
                <div className={`hidden flex-col items-center justify-center h-full bg-gradient-to-br ${biz.fallbackGrad} to-dark-900 gap-3 text-dark-500`}>
                  <i data-lucide={biz.fallbackIcon} className="text-4xl text-primary-500/40" />
                  <span className="text-xs">{biz.alt}</span>
                </div>
              </div>
              <div className="p-7">
                <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                  <i data-lucide={biz.icon} className={biz.iconColor} style={{ width: 20, height: 20 }} />
                  {t(biz.titleKey)}
                </h3>
                <p className="text-dark-400 text-sm" style={{ lineHeight: '1.8' }}>{t(biz.descKey)}</p>
                <div className="flex flex-wrap gap-2 mt-5">
                  {(t(biz.tagsKey, { returnObjects: true }) as string[]).map((tag: string, j: number) => (
                    <span key={j} className={`text-xs px-3 py-1 rounded-lg ${j === 2 ? 'bg-accent-500/10 text-accent-400' : 'bg-primary-500/10 text-primary-400'}`}>
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </FadeInSection>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BusinessSection;
